/**
 * DiscoveryHeroBanner — "pavé bleu" shown at the top of the dashboard
 * so users immediately spot the two highest-leverage features:
 *
 *   1. "Action en 1 clic" — direct transformations on their content
 *      (QCM, mail parents, DYS, plan de séance…).
 *   2. "Question de suivi" — chain a second question on the summary
 *      without consuming another entry from the free quota.
 *
 * UX contract
 *   - Dismissible via the close (X) button → persisted in localStorage
 *     (`sux1_hero_dismissed_at` ISO timestamp). Re-shows after 30 days
 *     so users rediscover the feature after a long absence.
 *   - Clicking either feature scrolls to the matching panel so users
 *     actually land on the control (not just read about it).
 *   - Hidden entirely once dismissed within the 30-day window.
 */
import React, { useState, useEffect } from "react";
import { useL } from "@/lib/useL";
import { Zap, CornerDownLeft, X } from "lucide-react";

const LS_KEY = "sux1_hero_dismissed_at";
const RESHOW_AFTER_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

function readDismissedWithin30d() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    if (!ts) return false;
    return Date.now() - ts < RESHOW_AFTER_MS;
  } catch {
    return false;
  }
}

export function DiscoveryHeroBanner() {
  const L = useL();
  const [hidden, setHidden] = useState(() => readDismissedWithin30d());

  // Hide once a different mount sets the flag (defensive — same tab).
  useEffect(() => {
    const sync = () => setHidden(readDismissedWithin30d());
    window.addEventListener("storage", sync);
    return () => window.removeEventListener("storage", sync);
  }, []);

  if (hidden) return null;

  const dismiss = () => {
    try { localStorage.setItem(LS_KEY, String(Date.now())); } catch {}
    setHidden(true);
  };

  // Scroll to the matching panel. Both panels are rendered later in the
  // Dashboard tree, so we query by their stable `data-testid`.
  const scrollToPanel = (testid) => {
    const el = document.querySelector(`[data-testid="${testid}"]`);
    if (el) {
      try { el.scrollIntoView({ behavior: "smooth", block: "center" }); } catch {}
    }
  };

  return (
    <div
      data-testid="dash-discovery-hero"
      className="mx-6 md:mx-8 lg:mx-10 mt-6 bg-[#002FA7] text-white border border-[#002FA7] shadow-sm"
    >
      <div className="flex items-start justify-between gap-3 px-5 py-4">
        <div className="font-mono uppercase tracking-[0.18em] text-[11px] text-white/90">
          {L("2 raccourcis pour aller plus vite", "2 shortcuts to go faster")}
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label={L("Fermer", "Close")}
          data-testid="dash-discovery-hero-dismiss"
          className="text-white/80 hover:text-white transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="px-5 pb-5 grid gap-3 sm:grid-cols-2">
        <button
          type="button"
          onClick={() => scrollToPanel("custom-instruction-panel")}
          data-testid="dash-discovery-cta-ci"
          className="text-left bg-white/[0.08] hover:bg-white/[0.14] border border-white/20 px-4 py-3 transition-colors group"
        >
          <div className="flex items-center gap-2 mb-1.5">
            <Zap className="w-4 h-4 shrink-0" />
            <span className="font-mono uppercase tracking-[0.14em] text-[11px]">
              {L("Action en 1 clic", "One-click action")}
            </span>
          </div>
          <div className="font-['IBM_Plex_Sans'] text-[13px] leading-relaxed text-white/90">
            {L(<>Appliquez une transformation directe (QCM, mail aux parents, DYS, plan de séance…) <b>sans passer par un résumé intermédiaire</b>.</>,
               <>Apply a direct transformation (quiz, email to parents, DYS, lesson plan…) <b>without an intermediate summary</b>.</>)}
          </div>
          <div className="mt-2 font-mono uppercase tracking-[0.14em] text-[10px] text-white/70 group-hover:text-white">
            {L("Ouvrir le panneau ↓", "Open the panel ↓")}
          </div>
        </button>
        <button
          type="button"
          onClick={() => scrollToPanel("followup-open-btn")}
          data-testid="dash-discovery-cta-followup"
          className="text-left bg-white/[0.08] hover:bg-white/[0.14] border border-white/20 px-4 py-3 transition-colors group"
        >
          <div className="flex items-center gap-2 mb-1.5">
            <CornerDownLeft className="w-4 h-4 shrink-0" />
            <span className="font-mono uppercase tracking-[0.14em] text-[11px]">
              {L("Question de suivi", "Follow-up question")}
            </span>
          </div>
          <div className="font-['IBM_Plex_Sans'] text-[13px] leading-relaxed text-white/90">
            {L(<>Enchaînez une 2ᵉ action après chaque résumé (QCM, reformulation, correction…) <b>pour seulement 1 crédit les 5 questions</b>.</>,
               <>Chain a 2nd action after each summary (quiz, rephrasing, correction…) <b>for just 1 credit per 5 questions</b>.</>)}
          </div>
          <div className="mt-2 font-mono uppercase tracking-[0.14em] text-[10px] text-white/70 group-hover:text-white">
            {L("Disponible après le 1ᵉʳ résumé", "Available after your 1st summary")}
          </div>
        </button>
      </div>
    </div>
  );
}

export default DiscoveryHeroBanner;
